import type { ChartOption } from "./chart-option.js"
import { ink, sequentialColor, surface, type ThemeMode } from "../palette.js"
import { CHART_TOOLTIP_CSS, FONT_STACK } from "../brand.js"
import { formatValue } from "../format.js"
import type { VizModel } from "../tile-model.js"
import { STALE_OPACITY } from "./base.js"

export const MAX_GAUGE_PANELS = 1

const START_ANGLE = 200
const END_ANGLE = -20

function gridOf(n: number): { cols: number; rows: number } {
  if (n <= 1) return { cols: 1, rows: 1 }
  const cols = Math.min(4, Math.ceil(Math.sqrt(n)))
  return { cols, rows: Math.ceil(n / cols) }
}

export type GaugeOptions = {
  mode: ThemeMode
  model: VizModel
  /** The scale maximum. 1 for `ratio`; other units must be supplied by the caller, and without one this should never have been reached */
  max?: number
}

export function gaugeOption({ mode, model, max = 1 }: GaugeOptions): ChartOption {
  const c = ink(mode)
  const s = model.series[0]
  const values = model.categories.map((label, i) => ({ label, value: s?.data[i] ?? null }))
  const { cols, rows } = gridOf(values.length)
  const stale = s?.state === "stale"

  const single = values.length === 1
  const cellW = 100 / cols
  const cellH = 100 / rows
  const radius = single ? "72%" : `${Math.min(cellW, cellH) * 0.42}%`

  const series = values.map((v, i) => {
    const col = i % cols
    const row = Math.floor(i / cols)
    const t = v.value == null ? 0 : Math.min(1, Math.max(0, v.value / max))

    return {
      type: "gauge" as const,
      name: v.label,
      startAngle: START_ANGLE,
      endAngle: END_ANGLE,
      min: 0,
      max,
      radius,
      center: single
        ? ["50%", "58%"]
        : [`${cellW * (col + 0.5)}%`, `${cellH * (row + 0.5) + 4}%`],
      axisLine: {
        lineStyle: {
          width: single ? 18 : 10,
          color: [
            [Math.max(t, 0.001), sequentialColor(0.35 + t * 0.5)],
            [1, c.gridline],
          ] as [number, string][],
        },
      },
      progress: { show: false },
      pointer: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
      itemStyle: { opacity: stale ? STALE_OPACITY : 1 },
      detail: {
        formatter: () => formatValue(v.value, { valueType: model.unit, context: "label" }),
        color: c.primary,
        fontSize: single ? 30 : 15,
        fontWeight: 650,
        offsetCenter: [0, single ? "-8%" : "-6%"],
      },
      title: {
        show: !single,
        color: c.secondary,
        fontSize: 11,
        offsetCenter: [0, "34%"],
        width: Math.floor((cellW / 100) * 260),
        overflow: "truncate" as const,
      },
      data: [{ value: v.value ?? 0, name: v.label }],
    }
  })

  return {
    backgroundColor: "transparent",
    animationDuration: 240,
    textStyle: {
      color: c.primary,
      fontFamily: FONT_STACK,
    },
    tooltip: {
      trigger: "item",
      backgroundColor: surface(mode),
      borderColor: c.gridline,
      borderWidth: 1,
      padding: [8, 10],
      textStyle: { color: c.primary, fontSize: 12 },
      extraCssText: CHART_TOOLTIP_CSS,
      formatter: (raw: unknown) => {
        const p = raw as { name?: string; seriesName?: string }
        const label = p.seriesName ?? p.name ?? ""
        const hit = values.find((v) => v.label === label)
        const value = formatValue(hit?.value ?? null, { valueType: model.unit, context: "label" })
        return `<div style="color:${c.secondary};font-size:11px;margin-bottom:2px">${label}</div>
          <strong style="color:${c.primary};font-variant-numeric:tabular-nums">${value}</strong>
          <span style="color:${c.secondary};font-size:11px;margin-left:4px">of ${formatValue(max, { valueType: model.unit, context: "label" })}</span>`
      },
    },
    legend: { show: false },
    series,
  }
}
